
import { Module } from '@/data/courses/types';
import { QuizQuestion } from '@/components/module/quiz/types';
import { standardizedQuizGenerator } from './standardizedQuizGenerator';

interface NormalizedQuiz {
  questions: QuizQuestion[];
  passThreshold: number;
  timeLimit?: number;
}

/**
 * Enhanced quiz normalizer that generates quizzes on-demand if missing
 */
export const normalizeQuizContentEnhanced = (module: Module, dbQuizData?: any, courseId?: string): NormalizedQuiz | null => {
  console.log('🔧 Normalizing quiz content for module:', module.id);
  
  // Priority 1: Check for DB quiz data first
  if (dbQuizData && Array.isArray(dbQuizData) && dbQuizData.length > 0) {
    console.log('✅ Found database quiz content');
    const questions = dbQuizData.map((q: any, index: number) => ({
      id: q.id || `${module.id}-q-${index}`,
      question: q.question,
      options: q.options,
      correctAnswer: q.correctAnswerIndex !== undefined ? q.correctAnswerIndex : (q.correctAnswer || 0),
      difficulty: q.difficulty || 'medium',
      explanation: q.explanation || 'No explanation provided.'
    }));

    return {
      questions,
      passThreshold: 75,
      timeLimit: 600
    };
  }
  
  // Priority 2: Check for quickCheck property (standard structure)
  if (module.quickCheck?.questions && module.quickCheck.questions.length > 0) {
    console.log('✅ Found quickCheck quiz content');
    const questions = module.quickCheck.questions.map((q, index) => ({
      id: q.id || `${module.id}-q-${index}`,
      question: q.question,
      options: q.options,
      correctAnswer: q.correctAnswerIndex !== undefined ? q.correctAnswerIndex : (q.correctAnswer || 0),
      difficulty: q.difficulty || 'medium',
      explanation: q.explanation || 'No explanation provided.'
    }));

    return {
      questions,
      passThreshold: module.quickCheck.passThreshold || 70,
      timeLimit: 600
    };
  }

  // Check for quiz property (alternative structure)
  if (module.quiz?.questions && module.quiz.questions.length > 0) {
    console.log('✅ Found quiz content');
    const questions = module.quiz.questions.map((q, index) => ({
      id: q.id || `${module.id}-q-${index}`,
      question: q.question,
      options: q.options,
      correctAnswer: q.correctAnswerIndex !== undefined ? q.correctAnswerIndex : (q.correctAnswer || 0),
      difficulty: q.difficulty || 'medium',
      explanation: q.explanation || 'No explanation provided.'
    }));

    return {
      questions,
      passThreshold: module.quiz.passThreshold || 70,
      timeLimit: 600
    };
  }

  // AUTO-GENERATE QUIZ IF MISSING
  console.log('🚀 No quiz found, auto-generating quiz for module:', module.id);
  
  try {
    // Infer course type from module structure or ID
    const courseType = inferCourseTypeFromModule(module);
    console.log('🎯 Inferred course type:', courseType);
    
    // Generate quiz using standardized generator
    const generatedQuiz = standardizedQuizGenerator.generateQuizForModule(
      module.id,
      courseType,
      module.title
    );
    
    console.log('✅ Auto-generated quiz with', generatedQuiz.questions.length, 'questions');
    
    return {
      questions: generatedQuiz.questions,
      passThreshold: generatedQuiz.passThreshold,
      timeLimit: generatedQuiz.timeLimit
    };
    
  } catch (error) {
    console.error('❌ Failed to auto-generate quiz:', error);
    
    // Ultimate fallback - generate basic generic quiz
    return generateBasicFallbackQuiz(module);
  }
};

/**
 * Infer course type from module data
 */
function inferCourseTypeFromModule(module: Module): string {
  // Check module ID patterns
  const moduleId = module.id.toLowerCase();
  const moduleTitle = module.title?.toLowerCase() || '';
  const moduleContent = (module.content || '').toLowerCase();
  
  // React patterns
  if (moduleId.includes('react') || moduleTitle.includes('react') || 
      moduleId.includes('jsx') || moduleTitle.includes('jsx') ||
      moduleId.includes('hook') || moduleTitle.includes('hook') ||
      moduleContent.includes('jsx') || moduleContent.includes('usestate')) {
    return 'react';
  }
  
  // SQL patterns
  if (moduleId.includes('sql') || moduleTitle.includes('sql') ||
      moduleId.includes('database') || moduleTitle.includes('database') ||
      moduleId.includes('query') || moduleTitle.includes('query') ||
      moduleContent.includes('select') || moduleContent.includes('database')) {
    return 'sql';
  }
  
  // Python patterns
  if (moduleId.includes('python') || moduleTitle.includes('python') ||
      moduleId.includes('py-') || moduleTitle.includes('py-') ||
      moduleContent.includes('def ') || moduleContent.includes('import ')) {
    return 'python';
  }
  
  // JavaScript patterns
  if (moduleId.includes('javascript') || moduleTitle.includes('javascript') ||
      moduleId.includes('js-') || moduleTitle.includes('js-') ||
      moduleId.includes('dom') || moduleTitle.includes('dom') ||
      moduleContent.includes('var ') || moduleContent.includes('let ') ||
      moduleContent.includes('const ') || moduleContent.includes('function')) {
    return 'javascript';
  }
  
  // AWS patterns
  if (moduleId.includes('aws') || moduleTitle.includes('aws') ||
      moduleId.includes('ec2') || moduleTitle.includes('ec2') ||
      moduleId.includes('s3') || moduleTitle.includes('s3') ||
      moduleContent.includes('aws') || moduleContent.includes('amazon')) {
    return 'aws';
  }
  
  // Azure patterns
  if (moduleId.includes('azure') || moduleTitle.includes('azure') ||
      moduleId.includes('microsoft') || moduleTitle.includes('microsoft') ||
      moduleContent.includes('azure') || moduleContent.includes('microsoft cloud')) {
    return 'azure';
  }
  
  // GCP patterns
  if (moduleId.includes('gcp') || moduleTitle.includes('gcp') ||
      moduleId.includes('google-cloud') || moduleTitle.includes('google cloud') ||
      moduleContent.includes('google cloud') || moduleContent.includes('bigquery')) {
    return 'gcp';
  }
  
  // Java patterns
  if (moduleId.includes('java') || moduleTitle.includes('java') ||
      moduleId.includes('spring') || moduleTitle.includes('spring') ||
      moduleContent.includes('public class') || moduleContent.includes('java')) {
    return 'java';
  }
  
  // .NET patterns
  if (moduleId.includes('dotnet') || moduleTitle.includes('dotnet') ||
      moduleId.includes('csharp') || moduleTitle.includes('c#') ||
      moduleId.includes('net') || moduleTitle.includes('.net') ||
      moduleContent.includes('using System') || moduleContent.includes('namespace')) {
    return 'dotnet';
  }
  
  // PowerBI patterns
  if (moduleId.includes('powerbi') || moduleTitle.includes('powerbi') ||
      moduleId.includes('power-bi') || moduleTitle.includes('power bi') ||
      moduleContent.includes('dax') || moduleContent.includes('power bi')) {
    return 'powerbi';
  }
  
  // Tableau patterns
  if (moduleId.includes('tableau') || moduleTitle.includes('tableau') ||
      moduleContent.includes('tableau') || moduleContent.includes('data visualization')) {
    return 'tableau';
  }
  
  // AI/ML patterns
  if (moduleId.includes('ai') || moduleTitle.includes('ai') ||
      moduleId.includes('machine-learning') || moduleTitle.includes('machine learning') ||
      moduleId.includes('ml-') || moduleTitle.includes('neural') ||
      moduleContent.includes('machine learning') || moduleContent.includes('artificial intelligence')) {
    return 'ai';
  }
  
  // Data Science patterns
  if (moduleId.includes('datascience') || moduleTitle.includes('data science') ||
      moduleId.includes('data-science') || moduleTitle.includes('analytics') ||
      moduleContent.includes('pandas') || moduleContent.includes('numpy') ||
      moduleContent.includes('data analysis')) {
    return 'datascience';
  }
  
  // Data Engineering patterns
  if (moduleId.includes('dataengineering') || moduleTitle.includes('data engineering') ||
      moduleId.includes('data-engineering') || moduleTitle.includes('etl') ||
      moduleContent.includes('etl') || moduleContent.includes('data pipeline')) {
    return 'dataengineering';
  }
  
  // Default fallback
  return 'basic';
}

/**
 * Generate a basic fallback quiz for any module
 */
function generateBasicFallbackQuiz(module: Module): NormalizedQuiz {
  console.log('🔄 Generating basic fallback quiz for:', module.id);
  
  const questions: QuizQuestion[] = [
    {
      id: `${module.id}-fallback-q1`,
      question: `What is the main focus of the "${module.title}" module?`,
      options: [
        "Learning fundamental concepts and principles",
        "Advanced implementation techniques",
        "Historical background information",
        "Unrelated topic discussion"
      ],
      correctAnswer: 0,
      difficulty: 'easy',
      explanation: "This module focuses on teaching core concepts and principles that are essential for understanding the topic."
    },
    {
      id: `${module.id}-fallback-q2`,
      question: "When learning new concepts, what is the most effective approach?",
      options: [
        "Memorize everything without understanding",
        "Practice regularly and apply the concepts",
        "Skip the difficult parts",
        "Only read about theory"
      ],
      correctAnswer: 1,
      difficulty: 'easy',
      explanation: "Active practice and application of concepts leads to better understanding and retention."
    },
    {
      id: `${module.id}-fallback-q3`,
      question: "Why is it important to understand the concepts covered in this module?",
      options: [
        "It's not important, just memorize",
        "They form the foundation for more advanced topics",
        "Only for passing tests",
        "To impress others"
      ],
      correctAnswer: 1,
      difficulty: 'medium',
      explanation: "Understanding foundational concepts is crucial as they serve as building blocks for more advanced learning and practical application."
    }
  ];

  return {
    questions,
    passThreshold: 70,
    timeLimit: 300 // 5 minutes for basic quiz
  };
}

// Helper function to validate quiz structure
export const validateQuizStructure = (quiz: NormalizedQuiz): boolean => {
  if (!quiz.questions || quiz.questions.length === 0) {
    return false;
  }

  return quiz.questions.every(q => 
    q.question && 
    q.options && 
    q.options.length >= 2 && 
    typeof q.correctAnswer === 'number' &&
    q.correctAnswer >= 0 &&
    q.correctAnswer < q.options.length
  );
};
